import React from "react";
import Markdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import CoffeeCardWeb from "../../../components/coffee_card_web/CoffeeCardWeb";
import Art from "../../../components/aotd-gallery/Art";
import { useAppSelector } from "../../../store/hooks";
import { BadgeArt } from "../../badges/art";
import { ordinal } from "../../badges/collection/badgeFormat";
import { TierName } from "../../badges/TierName";
import { timeAgo } from "../data/seed";
import type { SitelineBadge } from "../api/sitelineFeed";
import type { SitelineEntry } from "../types";
import Collapsible from "./Collapsible";
import LazyVisible from "./LazyVisible";

const KIND_ACCENT: Record<string, string> = {
  aotd: "var(--mint)",
  coffee: "var(--gold)",
  recipe: "var(--tangerine)",
  card: "var(--card-kind)",
  badge: "var(--pink)",
  announcement: "var(--mint)",
  gallery: "var(--sky)",
  blog: "var(--lavender)",
  comment: "var(--comment)",
};
const KIND_LABEL: Record<string, string> = {
  aotd: "aotd", coffee: "coffee", recipe: "recipe", card: "card",
  badge: "badge", announcement: "announce", gallery: "gallery", blog: "blog",
  comment: "comment",
};
const RARITY_ACCENT: Record<string, string> = {
  common: "var(--rare-common)",
  uncommon: "var(--rare-uncommon)",
  rare: "var(--rare-rare)",
  epic: "var(--rare-epic)",
  legendary: "var(--rare-legend)",
};

function kindAccent(e: SitelineEntry) { return KIND_ACCENT[e.kind] || "var(--ink)"; }
function chipLabel(e: SitelineEntry) { return KIND_LABEL[e.kind] || e.kind; }

type CardProps = { e: SitelineEntry; onOpen?: (e: SitelineEntry) => void; fresh?: boolean };

// ---------- header: emoji.username · time · kind chip ----------
function CardHeader({ e }: { e: SitelineEntry }) {
  const name = e.actor.system ? (e.actor.username || "sitebot") : e.actor.username;
  return (
    <div className="scard-head">
      <span className="actor-id">
        <span className="actor-emoji">{e.actor.emoji}</span>
        <span className="uname"><span className="udot">.</span>{name}</span>
      </span>
      <span className="head-meta">
        <span className="dotsep">·</span>
        <span className="tago">{timeAgo(e.occurredAt)}</span>
      </span>
      <span className="chip">{chipLabel(e)}</span>
    </div>
  );
}

function RarityTag({ rarity }: { rarity: string }) {
  return (
    <span className="rarity-tag" style={{ ["--rt" as any]: RARITY_ACCENT[rarity] || "var(--rare-common)" } as React.CSSProperties}>
      <span className="rt-diamond">◆</span>{rarity}
    </span>
  );
}

function SCard({
  e, children, onOpen, fresh, extraClass = "", clickable = true,
}: CardProps & { children: React.ReactNode; extraClass?: string; clickable?: boolean }) {
  return (
    <article
      className={"scard " + extraClass + (fresh ? " is-fresh" : "") + (clickable ? "" : " scard--static")}
      style={{ ["--ka" as any]: kindAccent(e) } as React.CSSProperties}
      onClick={clickable && onOpen ? () => onOpen(e) : undefined}
    >
      <CardHeader e={e} />
      <div className="scard-body">{children}</div>
    </article>
  );
}

// ============================================================ AOTD (gallery-style tile)
function AotdCard({ e, onOpen, fresh }: CardProps) {
  const p = e.payload || {};
  const navigate = useNavigate();
  const role = useAppSelector((s) => s.auth?.role);
  const dayKey = p.artInfo?.date ? new Date(p.artInfo.date).toISOString().slice(0, 10) : null;
  return (
    <article
      id={p.artInfo?.filename ? "sl-aotd-" + p.artInfo.filename : undefined}
      className={"scard" + (fresh ? " is-fresh" : "")}
      style={{ ["--ka" as any]: "var(--mint)" } as React.CSSProperties}
      onClick={() => onOpen && onOpen(e)}
    >
      <CardHeader e={e} />
      <div className="scard-body">
        <div className="aotd-gallery-tile">
          {p.artInfo && (
            <LazyVisible minHeight={380}>
              <Art artInfo={p.artInfo} />
            </LazyVisible>
          )}
        </div>
        <div className="aotd-links">
          <span className="aotd-link">view in gallery →</span>
          {role === "admin" && dayKey && (
            <button
              className="aotd-stats-link"
              onClick={(ev) => { ev.stopPropagation(); navigate(`/admin/aotd/${dayKey}`); }}
            >
              📊 full stats
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

// ============================================================ COFFEE (real card)
function CoffeeCard({ e, onOpen }: CardProps) {
  // The card chrome (header) deep-links; the embedded CoffeeCardWeb keeps its own
  // stats/map interactions via stopPropagation so taps there don't navigate.
  return (
    <SCard e={e} onOpen={onOpen}>
      <div className="coffee-embed" onClick={(ev) => ev.stopPropagation()}>
        {e.coffeeData && (
          <CoffeeCardWeb coffeeData={e.coffeeData} showDeleteButtons={false} showUserOverlay={false} />
        )}
      </div>
    </SCard>
  );
}

// ============================================================ RECIPE
function RecipeCard({ e, onOpen }: CardProps) {
  const p = e.payload || {};
  return (
    <SCard e={e} onOpen={onOpen}>
      <div className="row">
        {p.imageUrl && (
          <div className="thumb"><img className="thumb-img" src={p.imageUrl} alt={p.title} loading="lazy" /></div>
        )}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          <span className="body-headline">{p.title}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
            {p.difficulty && <span className="minipill">{p.difficulty}</span>}
            {p.time && <span className="body-sub">⏱ {p.time}</span>}
          </div>
          {p.description && (
            <Collapsible max={66}><div className="body-line">{p.description}</div></Collapsible>
          )}
        </div>
      </div>
    </SCard>
  );
}

// ============================================================ CARD PULL/PLAY
function CardPlayCard({ e, onOpen }: CardProps) {
  const p = e.payload || {};
  return (
    <SCard e={e} onOpen={onOpen}>
      <div className="row" style={{ alignItems: "center" }}>
        <span className="cardplay-emoji">{p.cardEmoji}</span>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 5 }}>
          <div className="cardplay-namerow">
            <span className="body-headline">{p.cardName}</span>
            {p.rarity && <RarityTag rarity={p.rarity} />}
          </div>
          <span className="body-sub" style={{ textTransform: "none" }}>{p.pulled ? "pulled from the drop" : "played a card"}</span>
          {p.effectSummary && <span className="body-line">{p.effectSummary}</span>}
          {p.targetUsername && <span className="arrow-target">→ <b>@{p.targetUsername}</b></span>}
        </div>
      </div>
    </SCard>
  );
}

// ============================================================ BADGES (one entry per member per day)
const BADGE_CHIPS_MAX = 5;

function BadgeFeedCard({ e, onOpen }: CardProps) {
  const p = e.payload || {};
  const badges: SitelineBadge[] = Array.isArray(p.badges) ? p.badges : [];
  const best = badges[0];
  if (!best) return null;
  const rest = badges.slice(1, 1 + BADGE_CHIPS_MAX);
  const hidden = badges.length - 1 - rest.length;
  return (
    <SCard e={e} onOpen={onOpen}>
      <div className="row" style={{ alignItems: "center" }}>
        <BadgeArt
          category={best.category}
          tier={best.tier}
          number={best.number}
          count={best.count > 1 ? best.count : undefined}
          code={best.code}
          name={best.name}
          size={52}
          variant="mini"
        />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 5 }}>
          <div className="cardplay-namerow">
            <span className="body-headline">
              <span style={{ fontWeight: 500 }}>earned </span>
              {best.name}
            </span>
            <TierName tier={best.tier} className="bdg-tiername--sm" />
          </div>
          {(best.repeatIndex > 1 || p.more > 0) && (
            <span className="body-sub" style={{ textTransform: "none" }}>
              {[
                best.repeatIndex > 1 ? `for the ${ordinal(best.repeatIndex)} time` : "",
                p.more > 0 ? `and ${p.more} more` : "",
              ]
                .filter(Boolean)
                .join(", ")}
            </span>
          )}
        </div>
      </div>
      {rest.length > 0 && (
        <div className="badge-grid">
          {rest.map((b) => (
            <span className="badge-chip" key={b.code + ":" + b.repeatIndex}>
              <BadgeArt
                category={b.category}
                tier={b.tier}
                number={b.number}
                code={b.code}
                name={b.name}
                size={20}
                variant="mini"
              />
              {b.name}
              {b.repeatIndex > 1 ? ` x${b.repeatIndex}` : ""}
            </span>
          ))}
          {hidden > 0 && <span className="badge-more">+{hidden} more</span>}
        </div>
      )}
    </SCard>
  );
}

// ============================================================ ANNOUNCEMENT
function AnnouncementCard({ e, onOpen, fresh }: CardProps) {
  const p = e.payload || {};
  const sections: Array<{ type?: string; highlight?: string }> = Array.isArray(p.sections) ? p.sections : [];
  return (
    <article
      className={"scard is-announce" + (fresh ? " is-fresh" : "")}
      style={{ ["--ka" as any]: "var(--mint)" } as React.CSSProperties}
      onClick={() => onOpen && onOpen(e)}
    >
      <CardHeader e={e} />
      <div className="scard-body">
        <div className="body-headline" style={{ fontSize: "calc(var(--fs-title) + 1px)" }}>{p.title}</div>
        <Collapsible max={92}>
          {p.content && <div className="ann-md"><Markdown>{p.content}</Markdown></div>}
          {sections.map((s, i) => (
            s.highlight ? <div className="ann-highlight" key={i}><Markdown>{s.highlight}</Markdown></div> : null
          ))}
        </Collapsible>
      </div>
    </article>
  );
}

// ============================================================ GALLERY
function GalleryCard({ e, onOpen }: CardProps) {
  const p = e.payload || {};
  return (
    <SCard e={e} onOpen={onOpen}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div>
          <span className="body-headline">{p.title}</span>
          <span className="muted" style={{ fontSize: "var(--fs-body)" }}> · added to the gallery</span>
        </div>
        {p.imageUrl && (
          <div className="thumb thumb-lg"><img className="thumb-img" src={p.imageUrl} alt={p.title} loading="lazy" /></div>
        )}
        {p.description && <div className="body-sub" style={{ textTransform: "none" }}>{p.description}</div>}
      </div>
    </SCard>
  );
}

// ============================================================ BLOG
function BlogCard({ e, onOpen }: CardProps) {
  const p = e.payload || {};
  return (
    <SCard e={e} onOpen={onOpen}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <span className="body-headline">{p.title}</span>
        {p.excerpt && (
          <Collapsible max={66}><div className="body-line">{p.excerpt}</div></Collapsible>
        )}
        <span className="aotd-link" style={{ borderColor: "rgb(var(--lavender))" }}>read →</span>
      </div>
    </SCard>
  );
}

// ============================================================ AOTD COMMENT
// Actor on top → the comment → a "go to the art of the day" button that scrolls
// down to that day's AOTD tile in the feed (then the AOTD card opens the art).
function CommentCard({ e, fresh }: CardProps) {
  const p = e.payload || {};
  const navigate = useNavigate();

  const goToAotd = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    const file = p.aotdFilename;
    const node = file ? document.getElementById("sl-aotd-" + file) : null;
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.classList.add("is-target-flash");
      window.setTimeout(() => node.classList.remove("is-target-flash"), 1600);
    } else {
      // that day's AOTD tile isn't in the loaded feed — fall back to the gallery.
      navigate("/art-of-the-day/gallery");
    }
  };

  return (
    <SCard e={e} fresh={fresh} clickable={false} extraClass="is-comment">
      <div className="cmt-text">{p.text}</div>
      {p.aotdTitle && (
        <div className="cmt-context">
          on <b>{p.aotdTitle}</b>
        </div>
      )}
      <button type="button" className="goto-aotd" onClick={goToAotd}>
        go to the art of the day <span className="goto-arrow">↓</span>
      </button>
    </SCard>
  );
}

const SitelineCard: React.FC<CardProps> = (props) => {
  switch (props.e.kind) {
    case "aotd": return <AotdCard {...props} />;
    case "coffee": return <CoffeeCard {...props} />;
    case "recipe": return <RecipeCard {...props} />;
    case "card": return <CardPlayCard {...props} />;
    case "badge": return <BadgeFeedCard {...props} />;
    case "announcement": return <AnnouncementCard {...props} />;
    case "gallery": return <GalleryCard {...props} />;
    case "blog": return <BlogCard {...props} />;
    case "comment": return <CommentCard {...props} />;
    default: return null;
  }
};

export default SitelineCard;
